import React, { memo, useEffect, useMemo, useState } from 'react';
import { Form, Select, InputNumber, Space } from 'antd';
import styled from 'styled-components';
import { CloseCircleOutlined } from '@ant-design/icons';
import { COLORS, TEXT_SIZES } from '../../../utils/constants';
import {
  createFacilitySportRequest,
  getSportsRequest,
  updateFacilitySportRequest,
} from '../../../api/requests/sports';
import {
  CreateSportType,
  EditSportType,
  FacilitySportDto,
  SportLiteType,
} from '../../../types/types';
import Button from '../../common/Button';
import Modal from '../../common/Modal';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { handleNumericKeyDown, parseNumericInput } from '../../../utils/utils';

interface SportFormValues {
  sportId: number;
  regularHourlyPrice: number;
  weekendHourlyPrice: number;
}

interface SportFormModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  facilityId: number;
  initialValues: FacilitySportDto | null;
  sportsOverview: FacilitySportDto[];
}

const SportModal: React.FC<SportFormModalProps> = ({
  visible,
  onClose,
  onSubmit,
  facilityId,
  initialValues,
  sportsOverview,
}) => {
  const [form] = Form.useForm<SportFormValues>();
  const snackbar = useSnackbar();
  const [sports, setSports] = useState<SportLiteType[]>([]);

  const fetchSports = async () => {
    try {
      const response = await getSportsRequest();
      if (response && response?.value) {
        if (sportsOverview && sportsOverview.length > 0) {
          const filteredSports = response.value.filter(
            (sport: SportLiteType) =>
              !sportsOverview.some(
                (overview) => overview.sportId === sport.sportId,
              ),
          );
          setSports(filteredSports);
          return;
        }
        setSports(response.value);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateSport = async () => {
    try {
      const values = await form.validateFields();
      if (values) {
        if (initialValues) {
          await updateSport(values);
        } else {
          await createSport(values);
        }
      }
    } catch (error) {
      console.warn('Validation failed:', error);
    }
  };

  const createSport = async (values: SportFormValues) => {
    try {
      const formattedValues: CreateSportType = {
        ...values,
        courts: [],
        facilityId: facilityId,
      };

      const response = await createFacilitySportRequest(formattedValues);
      if (response?.isSuccess) {
        onSubmit();
        form.resetFields();
        snackbar.success('Sport added successfully');
      } else {
        snackbar.error(response?.error);
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error adding sport to the facility');
    }
  };

  const updateSport = async (values: SportFormValues) => {
    try {
      const formattedValues: EditSportType = {
        ...values,
        courts: [],
        facilityId: facilityId,
        existingCourts: initialValues?.courts || [],
      };

      const response = await updateFacilitySportRequest(formattedValues);
      if (response?.isSuccess) {
        onSubmit();
        form.resetFields();
        snackbar.success('Sport updated successfully');
      } else {
        snackbar.error(response?.error);
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error updating sport to the facility');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const CloseIcon = useMemo(() => <StyledCloseIcon />, []);

  useEffect(() => {
    if (!initialValues) fetchSports();
  }, [initialValues]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  return (
    <Modal
      title={initialValues ? 'Edit Sport' : 'Add New Sport'}
      open={visible}
      onCancel={handleCancel}
      closeIcon={CloseIcon}
      minHeight={'auto'}
    >
      <Form form={form} layout="vertical">
        <StyledFormItem
          name="sportId"
          label="Sport Name"
          rules={[{ required: true, message: 'Please select a sport' }]}
        >
          <StyledSelect
            placeholder="Select"
            size="large"
            disabled={!!initialValues}
          >
            {sports?.map((sport: SportLiteType) => (
              <Select.Option key={sport.sportId} value={sport.sportId}>
                {sport.sportName}
              </Select.Option>
            ))}
          </StyledSelect>
        </StyledFormItem>

        <StyledHoursSpace>
          <StyledFormItem
            name="regularHourlyPrice"
            label="Weekday(₹/hr)"
            rules={[{ required: true, message: 'Please enter hourly rate' }]}
          >
            <StyledInputNumber
              min={0}
              placeholder="499"
              size="large"
              parser={parseNumericInput}
              onKeyDown={handleNumericKeyDown}
            />
          </StyledFormItem>
          <StyledFormItem
            name="weekendHourlyPrice"
            label="Weekend(₹/hr)"
            rules={[{ required: true, message: 'Please enter weekend rate' }]}
          >
            <StyledInputNumber
              min={0}
              placeholder="799"
              size="large"
              parser={parseNumericInput}
              onKeyDown={handleNumericKeyDown}
            />
          </StyledFormItem>
        </StyledHoursSpace>

        <ButtonSection>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleCreateSport}>
            {initialValues ? 'Update Sport' : 'Add Sport'}
          </Button>
        </ButtonSection>
      </Form>
    </Modal>
  );
};

const StyledFormItem = styled(Form.Item)`
  width: 100%;
  &.ant-space-item {
    flex: 1;
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 32px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    height: 40px !important;
    align-items: center;
    display: flex;
  }
  .ant-select-selection-item {
    line-height: 40px !important;
  }
`;

const StyledCloseIcon = styled(CloseCircleOutlined)`
  font-size: ${TEXT_SIZES.XXL}px;
  color: ${COLORS.white.primary};
`;

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
`;

const StyledHoursSpace = styled(Space)`
  width: 100%;
  display: flex;
  margin-top: 16px;
  gap: 24px;
  .ant-space-item {
    flex: 1;
  }
`;

export default memo(SportModal);
